import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";

// Components
import CustomButton from "./CustomButton";

// Styles
import styles from "./modal.module.scss";
import Typography from "./Typography";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string | React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  showCloseButton?: boolean;
  classes?: {
    backdrop?: string;
    content?: string;
    header?: string;
    title?: string;
  };
  footer?: React.ReactNode;
  tabLines?: React.ReactNode;
}

export default function Modal({
  open,
  onClose,
  title,
  children,
  className = '',
  style = {},
  classes = {},
  showCloseButton = false,
  footer = null,
  tabLines = null
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Focus trap
  useEffect(() => {
    if (!open) return;
    const focusable = modalRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable && focusable.length) {
      focusable[0].focus();
    }
  }, [open]);

  if (!open) return null;

  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  const renderHeader = () => {
    if (typeof title === 'string') {
      return (
        <div className={`${styles.header} ${classes.header}`}>
          <Typography
            variant="h2"
            className={`${styles.title} ${classes.title}`}
          >
            {title}
          </Typography>

          {showCloseButton && (
            <CustomButton variant="text" classes={{ root: styles.closeButton }} onClick={onClose}>
              <IoClose />
            </CustomButton>
          )}
        </div>
      )
    }

    return title;
  }

  return createPortal(
    <div
      className={`${styles.backdrop} ${classes.backdrop}`}
      onClick={handleBackdropClick}
    >
      <div
        className={`${styles.content} ${classes.content} ${className}`.trim()}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
        style={style}
      >
        {renderHeader()}
        {tabLines}
        <div>
          {children}
        </div>

        {footer}
      </div>
    </div>,
    document.body
  );
}
